import { TrackerEventType } from "src/core/model/TrackerEventType";
import { TrackerVariableType } from "src/core/model/TrackerVariableType";

import { EventListener } from "./EventListener";

export type NativeEventTrigger = (type: TrackerEventType) => void;

/**
 * Listens for page navigation events (popstate, pushState, replaceState).
 * Mirrors Android ActivityLifecycleListener for PAGE_VIEW.
 */
export class PageViewListener implements EventListener {
  private trigger: NativeEventTrigger;
  private started = false;
  private boundPopstate: (() => void) | null = null;
  private origPushState: typeof history.pushState | null = null;
  private origReplaceState: typeof history.replaceState | null = null;

  constructor(trigger: NativeEventTrigger) {
    this.trigger = trigger;
  }

  start(): void {
    if (this.started) return;
    this.started = true;

    // popstate
    this.boundPopstate = () => this.trigger(TrackerEventType.PAGE_VIEW);
    window.addEventListener("popstate", this.boundPopstate);

    // Patch pushState/replaceState to detect SPA navigation
    this.origPushState = history.pushState.bind(history);
    this.origReplaceState = history.replaceState.bind(history);

    const origPush = this.origPushState;
    const origReplace = this.origReplaceState;
    const trigger = () => this.trigger(TrackerEventType.PAGE_VIEW);
    history.pushState = function (...args: Parameters<typeof history.pushState>) {
      if (origPush) origPush(...args);
      trigger();
    };
    history.replaceState = function (...args: Parameters<typeof history.replaceState>) {
      if (origReplace) origReplace(...args);
      trigger();
    };
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;

    if (this.boundPopstate) {
      window.removeEventListener("popstate", this.boundPopstate);
      this.boundPopstate = null;
    }
    if (this.origPushState) {
      history.pushState = this.origPushState;
      this.origPushState = null;
    }
    if (this.origReplaceState) {
      history.replaceState = this.origReplaceState;
      this.origReplaceState = null;
    }
  }

  getTypes(): TrackerEventType[] {
    return [TrackerEventType.PAGE_VIEW];
  }

  getVariableTypes(): TrackerVariableType[] {
    return [TrackerVariableType.URL, TrackerVariableType.VIEW_DURATION];
  }
}
